import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const SESSION_TIMEOUT_MINUTES = Number(process.env.REACT_APP_JWT_TOKEN_TIMEOUT || 0);

const getSessionTimeoutLabel = () => {
  if (!Number.isFinite(SESSION_TIMEOUT_MINUTES) || SESSION_TIMEOUT_MINUTES <= 0) {
    return null;
  }
  if (SESSION_TIMEOUT_MINUTES % 60 === 0) {
    const hours = SESSION_TIMEOUT_MINUTES / 60;
    return `${hours}-hour`;
  }
  return `${SESSION_TIMEOUT_MINUTES}-minute`;
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('API Request:', config.method.toUpperCase(), config.url, config.data);
    console.log('Auth Token:', token ? 'Present' : 'Missing');
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.config.method.toUpperCase(), response.config.url, response.status);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear all auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Show user-friendly message about session expiration
      if (typeof window !== 'undefined') {
        const message = error.response?.data?.detail || 'Your session has expired';
        
        // Check if it's specifically an expiration message
        if (message.includes('expired') || message.includes('validate credentials')) {
          // Create a temporary session expired message
          const sessionExpiredDiv = document.createElement('div');
          const timeoutLabel = getSessionTimeoutLabel();
          const timeoutText = timeoutLabel
            ? `Session Expired: Your ${timeoutLabel} session has ended. Please login again.`
            : 'Session Expired: Please login again.';
          sessionExpiredDiv.innerHTML = `
            <div style="
              position: fixed;
              top: 20px;
              right: 20px;
              background: #ef4444;
              color: white;
              padding: 12px 20px;
              border-radius: 8px;
              z-index: 9999;
              font-family: system-ui;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            ">
              ${timeoutText}
            </div>
          `;
          document.body.appendChild(sessionExpiredDiv);
          
          // Remove message after 3 seconds and redirect
          setTimeout(() => {
            if (sessionExpiredDiv.parentNode) {
              sessionExpiredDiv.parentNode.removeChild(sessionExpiredDiv);
            }
            // Force redirect to login page
            window.location.href = '/';
          }, 3000);
        } else {
          // For other 401 errors, redirect immediately
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

// API Service Methods
export const api = {
  // Auth
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  getMe: () => apiClient.get('/auth/me'),

  // Profile Management
  updateProfile: (profileData) => apiClient.put('/profile/update', profileData),
  getProfile: () => apiClient.get('/profile/'),
  changePassword: (passwordData) => apiClient.put('/profile/change-password', passwordData),

  // Admin - Users
  getUsers: (page = 1, limit = 10, search = '') =>
    apiClient.get(`/admin-enhanced/users?page=${page}&limit=${limit}&search=${search}`),
  createUser: (userData) => apiClient.post('/admin-enhanced/users', userData),
  updateUser: (userId, userData) => apiClient.put(`/admin-enhanced/users/${userId}`, userData),
  deleteUser: (userId) => apiClient.delete(`/admin-enhanced/users/${userId}`),
  updateUserRole: (userId, role) => apiClient.put(`/admin-enhanced/users/${userId}`, { role }),
  bulkUserOperation: (data) => apiClient.post('/admin-enhanced/users/bulk', data),

  // Admin - Dashboard
  getAdminDashboard: () => apiClient.get('/admin-enhanced/dashboard'),
  getSystemSettings: () => apiClient.get('/admin-enhanced/settings'),
  updateSystemSettings: (settings) => apiClient.put('/admin-enhanced/settings', settings),
  getActivityLogs: (page = 1, limit = 50) =>
    apiClient.get(`/admin-enhanced/activity-logs?page=${page}&limit=${limit}`),
  getRevenueReports: (period = 'month') =>
    apiClient.get(`/admin-enhanced/revenue-reports?period=${period}`),

  // Courses
  getCourses: (page = 1, limit = 10, filters = {}) => {
    const params = new URLSearchParams({ page, limit, ...filters });
    return apiClient.get(`/courses?${params}`);
  },
  getCourse: (courseId) => apiClient.get(`/courses/${courseId}`),
  getCourseBatches: (courseId) => apiClient.get(`/courses/${courseId}/batches`),
  getInstructorAssignedBatches: async () => {
    const response = await apiClient.get('/instructor/assignments');
    return { data: response?.data?.batches || [] };
  },
  createCourse: (courseData) => apiClient.post('/courses', courseData),
  updateCourse: (courseId, courseData) => apiClient.put(`/courses/${courseId}`, courseData),
  deleteCourse: (courseId) => apiClient.delete(`/courses/${courseId}`),
  enrollCourse: (courseId) => apiClient.post(`/enrollments/${courseId}`),
  getCourseModules: (courseId) => apiClient.get(`/courses/${courseId}/modules`),

  // Get courses filtered by runway_ids and/or batch_ids
  getCoursesByFilter: (runwayIds = null, batchIds = null) => {
    const params = new URLSearchParams();
    if (runwayIds && runwayIds.length > 0) {
      params.append('runway_ids', runwayIds.join(','));
    }
    if (batchIds && batchIds.length > 0) {
      params.append('batch_ids', batchIds.join(','));
    }
    const queryString = params.toString();
    return apiClient.get(`/courses/filter${queryString ? '?' + queryString : ''}`);
  },

  // Modules
  getModules: (courseId) => apiClient.get(`/courses/${courseId}/modules`),
  getAllModules: () => apiClient.get('/modules'),
  getModule: (moduleId) => apiClient.get(`/modules/${moduleId}`),
  createModule: (moduleData) => apiClient.post('/modules', moduleData),
  updateModule: (moduleId, moduleData) => apiClient.put(`/modules/${moduleId}`, moduleData),
  deleteModule: (moduleId) => apiClient.delete(`/modules/${moduleId}`),

  // Lessons
  getLessons: (moduleId) => apiClient.get(`/modules/${moduleId}/lessons`),
  getLesson: (lessonId) => apiClient.get(`/lessons/${lessonId}`),
  createLesson: (lessonData) => apiClient.post('/lessons', lessonData),
  updateLesson: (lessonId, lessonData) => apiClient.put(`/lessons/${lessonId}`, lessonData),
  deleteLesson: (lessonId) => apiClient.delete(`/lessons/${lessonId}`),

  // Topics
  getTopics: (lessonId) => apiClient.get(`/lessons/${lessonId}/topics`),
  getTopic: (topicId) => apiClient.get(`/topics/${topicId}`),
  createTopic: (topicData) => apiClient.post('/topics', topicData),
  updateTopic: (topicId, topicData) => apiClient.put(`/topics/${topicId}`, topicData),
  deleteTopic: (topicId) => apiClient.delete(`/topics/${topicId}`),

  // Content
  getContent: (contentId) => apiClient.get(`/content/${contentId}`),
  getContentByTopic: (topicId) => apiClient.get(`/content/${topicId}`),
  getContents: (topicId) => apiClient.get(`/topics/${topicId}/contents`),
  createContent: (contentData) => apiClient.post('/content', contentData),
  updateContent: (contentId, contentData) => apiClient.put(`/content/${contentId}`, contentData),
  deleteContent: (contentId) => apiClient.delete(`/content/${contentId}`),
  getContentReleases: (courseId, batchId = null) => {
    const params = { course_id: courseId };
    if (batchId) params.batch_id = batchId;
    return apiClient.get('/content-release', { params });
  },
  releaseContent: (payload) => apiClient.post('/content-release', payload),
  unreleaseContent: (courseId, contentId, batchId = null) => {
    const params = { course_id: courseId, content_id: contentId };
    if (batchId) params.batch_id = batchId;
    return apiClient.delete('/content-release', { params });
  },
    markContentReleaseLiveClass: (releaseId, onlineClassId = null, created = true) =>
      apiClient.post(`/content-release/${releaseId}/online-class`, {
        online_class_id: onlineClassId,
        created,
      }),

  // Live Classes
  createLiveClass: (liveClassData) => apiClient.post('/live-classes', liveClassData),
  updateLiveClass: (classId, liveClassData) => apiClient.put(`/live-classes/${classId}`, liveClassData),
  getLiveClasses: (courseId = null, page = 1, limit = 10, filters = {}) => {
    const params = new URLSearchParams();
    if (courseId) params.append('course_id', courseId);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    if (filters.search) params.append('search', filters.search);
    if (filters.batch_id != null) params.append('batch_id', filters.batch_id);
    if (filters.runway_id != null) params.append('runway_id', filters.runway_id);
    return apiClient.get(`/live-classes?${params.toString()}`);
  },
  getLiveClass: (classId) => apiClient.get(`/live-classes/${classId}`),
  deleteLiveClass: (classId) => apiClient.delete(`/live-classes/${classId}`),
  getAttendanceSessions: (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/live-classes/attendance/sessions${queryString ? `?${queryString}` : ''}`);
  },
  getAttendanceOverview: (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/live-classes/attendance/overview${queryString ? `?${queryString}` : ''}`);
  },
  getLiveClassAttendance: (classId, params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/live-classes/${classId}/attendance${queryParams ? `?${queryParams}` : ''}`);
  },
  markAttendance: (classId, attendanceData) => apiClient.post(`/live-classes/${classId}/attendance/mark`, attendanceData),
  joinLiveClass: (classId) => apiClient.post(`/live-classes/${classId}/join`),
  getAdminRecordings: (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/recordings/admin${queryParams ? `?${queryParams}` : ''}`);
  },
  updateRecordingStatus: (recordingId, payload) => apiClient.put(`/recordings/${recordingId}/status`, payload),
  deleteRecording: (recordingId) => apiClient.delete(`/recordings/${recordingId}`),
  getRecordings: () => apiClient.get('/recordings'),
  getRecording: (recordingId) => apiClient.get(`/recordings/${recordingId}`),
  getRecordingNotes: (recordingId) => apiClient.get(`/recordings/${recordingId}/notes`),
  saveRecordingNotes: (recordingId, notes) => apiClient.put(`/recordings/${recordingId}/notes`, { notes }),
  getRecordingPresignedUrl: (recordingId) => apiClient.get(`/recordings/${recordingId}/presigned-url`),
  markRecordingComplete: (recordingId, watchDuration) =>
    apiClient.post(`/recordings/${recordingId}/complete?watch_duration=${watchDuration}`),

  // File Uploads
  uploadContentImage: (formData) => apiClient.post('/upload/content-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadContentFile: (formData) => apiClient.post('/upload/content-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadScormPackage: (formData) => apiClient.post('/upload/scorm-package', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFile: (formData) => apiClient.post('/upload/file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Generic methods for direct API calls
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  get: (url, config = {}) => apiClient.get(url, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),

  getCourseManagementPaths: (page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    return apiClient.get(`/course-management/paths?${params}`);
  },
  createCourseManagementPath: (pathData) => apiClient.post('/course-management/paths', pathData),
  updateCourseManagementPath: (pathId, pathData) => apiClient.put(`/course-management/paths/${pathId}`, pathData),
  deleteCourseManagementPath: (pathId) => apiClient.delete(`/course-management/paths/${pathId}`),

  // Jobs
  getJobs: (page = 1, limit = 10, filters = {}) => {
    const params = new URLSearchParams({ page, limit, ...filters });
    return apiClient.get(`/jobs-enhanced/listings?${params}`);
  },
  getJob: (jobId) => apiClient.get(`/jobs-enhanced/listings/${jobId}`),
  createJob: (jobData) => apiClient.post('/jobs', jobData),
  updateJob: (jobId, jobData) => apiClient.put(`/jobs/${jobId}`, jobData),
  deleteJob: (jobId) => apiClient.delete(`/jobs/${jobId}`),
  applyJob: (jobId, applicationData) => apiClient.post(`/jobs-enhanced/listings/${jobId}/apply`, applicationData),
  getMyApplications: (page = 1) => apiClient.get(`/jobs-enhanced/my-applications?page=${page}`),
  getCompanies: (page = 1) => apiClient.get(`/jobs-enhanced/companies?page=${page}`),

  // Community
  getForumPosts: (page = 1, limit = 20, category = '') => {
    const params = new URLSearchParams({ page, limit });
    if (category) params.append('category', category);
    return apiClient.get(`/community/posts?${params}`);
  },
  getPost: (postId) => apiClient.get(`/community/posts/${postId}`),
  createPost: (postData) => apiClient.post('/community/posts', postData),
  updatePost: (postId, postData) => apiClient.put(`/community/posts/${postId}`, postData),
  deletePost: (postId) => apiClient.delete(`/community/posts/${postId}`),
  getReplies: (postId, page = 1) => apiClient.get(`/community/posts/${postId}/replies?page=${page}`),
  createReply: (postId, content) => apiClient.post(`/community/posts/${postId}/replies`, { content }),
  reactToPost: (postId, reactionType) => apiClient.post(`/community/posts/${postId}/reactions`, { reaction_type: reactionType }),

  // Notifications
  getNotifications: (page = 1, limit = 20, unreadOnly = false, readOnly = false, notificationType = null) => {
    const payload = {
      page,
      limit,
      unread_only: unreadOnly,
      read_only: readOnly,
    };
    if (notificationType) payload.notification_type = notificationType;
    return apiClient.post('/notifications-enhanced/list', payload);
  },
  getUnreadNotificationCount: () => apiClient.get('/notifications-enhanced/unread-count'),
  markNotificationRead: (notificationId) => apiClient.put(`/notifications-enhanced/${notificationId}/read`),
  markAllRead: () => apiClient.put('/notifications-enhanced/mark-all-read'),
  deleteNotification: (notificationId) => apiClient.delete(`/notifications-enhanced/${notificationId}`),
  bulkDeleteReadNotifications: () => apiClient.delete('/notifications-enhanced/bulk-delete-read'),
  getNotificationPreferences: () => apiClient.get('/notifications-enhanced/preferences'),
  updateNotificationPreferences: (preferences) => apiClient.put('/notifications-enhanced/preferences', preferences),

  // Assessments
  getTests: (page = 1, courseId = null) => {
    const params = new URLSearchParams({ page, limit: 10 });
    if (courseId) params.append('course_id', courseId);
    return apiClient.get(`/tests?${params}`);
  },
  getTest: (testId) => apiClient.get(`/tests/${testId}`),
  submitTest: (testId, answers) => apiClient.post(`/tests/${testId}/submit`, { answers }),
  getTestResults: (testId) => apiClient.get(`/tests/${testId}/results`),
  getAdminAssessments: (params = {}) => {
    // Filter out undefined and null values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/admin-assessments/${queryParams ? `?${queryParams}` : ''}`);
  },
  getAdminAssessmentMeta: () => apiClient.get('/admin-assessments/meta'),
  getFilteredInstructors: (batchId = null, runwayId = null) => {
    const params = new URLSearchParams();
    if (batchId) params.append('batch_id', batchId);
    if (runwayId) params.append('runway_id', runwayId);
    const queryString = params.toString();
    return apiClient.get(`/admin-assessments/instructors${queryString ? '?' + queryString : ''}`);
  },
  getCoursesByRunway: (runwayId) => apiClient.get(`/admin-assessments/courses/by-runway/${runwayId}`),
  getAdminAssessment: (assessmentId) => apiClient.get(`/admin-assessments/${assessmentId}`),
  createAdminAssessment: (formData) => apiClient.post('/admin-assessments/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateAdminAssessment: (assessmentId, formData) => apiClient.put(`/admin-assessments/${assessmentId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAdminAssessment: (assessmentId) => apiClient.delete(`/admin-assessments/${assessmentId}`),
  getStudentAssessments: (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/student-assessments${queryString ? `?${queryString}` : ''}`);
  },
  getStudentAssessment: (assessmentId) => apiClient.get(`/student-assessments/${assessmentId}`),
  submitStudentAssessment: (assessmentId, formData) =>
    apiClient.post(`/student-assessments/${assessmentId}/submit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  getStudentAssessmentResult: (assessmentId) => apiClient.get(`/student-assessments/${assessmentId}/result`),
  getInstructorAssignedAssessments: (params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/student-assessments/instructor/assigned${queryParams ? `?${queryParams}` : ''}`);
  },
  getInstructorAssessmentSubmissions: (assessmentId, params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null)
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return apiClient.get(`/student-assessments/instructor/${assessmentId}/submissions${queryParams ? `?${queryParams}` : ''}`);
  },
  getInstructorAssessmentSubmission: (assessmentId, submissionId) =>
    apiClient.get(`/student-assessments/instructor/${assessmentId}/submissions/${submissionId}`),
  publishInstructorEvaluation: (assessmentId, submissionId, formData) =>
    apiClient.put(`/student-assessments/instructor/${assessmentId}/submissions/${submissionId}/evaluate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getInstructorDashboard: () => apiClient.get('/instructors/dashboard'),

  getAssignments: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiClient.get(`/assignments${queryParams ? `?${queryParams}` : ''}`);
  },
  getAssignment: (assignmentId) => apiClient.get(`/assignments/${assignmentId}`),
  createAssignment: (assignmentData) => apiClient.post('/assignments', assignmentData),
  updateAssignment: (assignmentId, assignmentData) => apiClient.put(`/assignments/${assignmentId}`, assignmentData),
  deleteAssignment: (assignmentId) => apiClient.delete(`/assignments/${assignmentId}`),
  submitAssignmentFile: (assignmentId, formData) =>
    apiClient.post(`/assignments/${assignmentId}/submit-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  gradeSubmission: (submissionId, gradeData) =>
    apiClient.put(`/assignments/submissions/${submissionId}/grade`, gradeData),
  getAssignmentDistributions: (assignmentId) => apiClient.get(`/assignment-distributions/assignment/${assignmentId}`),
  getAssignmentExamReport: (assignmentId, batchId, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('batch_id', batchId);
    const normalizedParams = { page: 1, size: 25, ...params };
    Object.entries(normalizedParams).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'string' && value.trim() === '') return;
      queryParams.append(key, value);
    });
    return apiClient.get(`/assignment-distributions/assignment/${assignmentId}/exam-report?${queryParams.toString()}`);
  },

  // Assignment Batch Progress List
  getAssignmentBatchProgressList: (assignmentId, page = 1, size = 10) => 
    apiClient.get(`/assignment-distributions/assignment/${assignmentId}/batch-progress-list?page=${page}&size=${size}`),

  // Payments (Stripe)
  createCheckoutSession: (items) => apiClient.post('/payments/create-checkout-session', { items }),
  getSubscriptions: () => apiClient.get('/payments/subscriptions'),
  cancelSubscription: (subscriptionId) => apiClient.delete(`/payments/subscriptions/${subscriptionId}`),
  getPaymentHistory: (page = 1) => apiClient.get(`/payments/history?page=${page}`),

  // Runways
  getRunways: (page = 1, limit = 10, search = '') => apiClient.get(`/runways/?page=${page}&limit=${limit}&search=${search}`),
  getRunway: (runwayId) => apiClient.get(`/runways/${runwayId}`),
  createRunway: (runwayData) => apiClient.post('/runways/', runwayData),
  updateRunway: (runwayId, runwayData) => apiClient.put(`/runways/${runwayId}`, runwayData),
  deleteRunway: (runwayId) => apiClient.delete(`/runways/${runwayId}`),
  getInstituteCourses: () => apiClient.get('/courses'),
  getRunwaysForAssignment: (assignmentId) => apiClient.get(`/runways/assignments/${assignmentId}/runways`),

  // Batches
  getBatches: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.batch_type) params.append('batch_type', filters.batch_type);
    if (filters.runway_id) params.append('runway_id', filters.runway_id);
    if (filters.runway_ids) params.append('runway_ids', filters.runway_ids.join(','));
    if (filters.status_filter) params.append('status_filter', filters.status_filter);
    if (filters.institute_id) params.append('institute_id', filters.institute_id);
    if (filters.search) params.append('search', filters.search);
    if (filters.year) params.append('year', filters.year);

    const queryString = params.toString();
    return apiClient.get(`/batches/${queryString ? '?' + queryString : ''}`);
  },
  getBatch: (batchId) => apiClient.get(`/batches/${batchId}`),
  createBatch: (batchData) => apiClient.post('/batches/', batchData),
  updateBatch: (batchId, batchData) => apiClient.put(`/batches/${batchId}`, batchData),
  deleteBatch: (batchId) => apiClient.delete(`/batches/${batchId}`),
  getRunwayOptions: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiClient.get(`/batches/runways/options?${searchParams.toString()}`)
  },
  getBatchCourses: (batchId) => apiClient.get(`/api/batch-courses/${batchId}/courses`),
  updateBatchCourseStatus: (batchId, courseId, status) => apiClient.put('/api/batch-courses/status', { batch_id, course_id, status }),

  // Runways (new endpoints for custom runway creation)
  getRunwayList: () => apiClient.get('/runways/'),
  createCustomRunway: (runwayData) => apiClient.post('/runways', runwayData),

  // Question Types
  getQuestionTypes: () => apiClient.get('/question-types/'),
  getQuestionType: (id) => apiClient.get(`/question-types/${id}/`),
  getQuestionTypeBySlug: (slug) => apiClient.get(`/question-types/slug/${slug}/`),

  // Modules (already defined above with courseId parameter)
  getModule: (moduleId) => apiClient.get(`/modules/${moduleId}`),

  // Lessons (already defined above)
  getLesson: (lessonId) => apiClient.get(`/lessons/${lessonId}`),

  // Topics (already defined above)
  getTopic: (topicId) => apiClient.get(`/topics/${topicId}`),

  // Questions
  createQuestion: (questionData) => apiClient.post('/questions', questionData),
  getQuestions: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const fullUrl = `/questions${queryParams ? `?${queryParams}` : ''}`;
    return apiClient.get(fullUrl);
  },
  getQuestion: (questionId) => apiClient.get(`/questions/${questionId}`),
  updateQuestion: (questionId, questionData) => apiClient.put(`/questions/${questionId}`, questionData),
  deleteQuestion: (questionId) => apiClient.delete(`/questions/${questionId}`),

  // Programming Evaluation
  executeCode: (executionData) => apiClient.post('/programming/execute', executionData),
  evaluateProgrammingSubmission: (submissionData) => apiClient.post('/programming/evaluate', submissionData),
  getProgrammingLanguages: () => apiClient.get('/programming/languages'),

  // Assignment Questions
  addQuestionToAssignment: (assignmentQuestionData) => apiClient.post('/assignment-questions', assignmentQuestionData),
  getAssignmentQuestions: (assignmentId) => apiClient.get(`/assignments/${assignmentId}/questions`),
  removeQuestionFromAssignment: (assignmentId, questionId) => apiClient.delete(`/assignment-questions/${assignmentId}/${questionId}`),

  // Assignment Distributions
  createAssignmentDistribution: (distributionData) => apiClient.post('/assignment-distributions/', distributionData),
  getAssignmentDistributions: (assignmentId) => apiClient.get(`/assignment-distributions/assignment/${assignmentId}`),
  getAssignmentOverallPerformance: (assignmentId) => apiClient.get(`/assignment-distributions/assignment/${assignmentId}/overall-performance`),
  getStudentAssignments: (studentId) => apiClient.get(`/assignment-distributions/student-assignments/${studentId}`),
  getStudentEligibility: (runwayIds, batchIds, courseIds) => {
    const params = new URLSearchParams();
    if (runwayIds && runwayIds.length > 0) params.append('runway_ids', runwayIds.join(','));
    if (batchIds && batchIds.length > 0) params.append('batch_ids', batchIds.join(','));
    if (courseIds && courseIds.length > 0) params.append('course_ids', courseIds.join(','));
    return apiClient.get(`/assignment-distributions/eligibility${params.toString() ? '?' + params.toString() : ''}`);
  },
  updateAssignmentDistribution: (distributionId, distributionData) =>
    apiClient.put(`/assignment-distributions/${distributionId}`, distributionData),
  deleteAssignmentDistribution: (distributionId) => apiClient.delete(`/assignment-distributions/${distributionId}`),
  // Course Assignment - Students
  getStudents: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.run_way_id) params.append('run_way_id', filters.run_way_id);
    if (filters.batch_id) params.append('batch_id', filters.batch_id);
    if (filters.institute_id) params.append('institute_id', filters.institute_id);
    if (filters.role) params.append('role', filters.role);
    const queryString = params.toString();
    return apiClient.get(`/students${queryString ? `?${queryString}` : ''}`);
  },
  createCourseAssignment: (assignmentData) => apiClient.post('/course-assignments', assignmentData),
  getCourseAssignments: (courseId) => apiClient.get(`/course-assignments/${courseId}`),
  getStudentCourses: (studentId) => apiClient.get(`/student-courses/${studentId}`),
  getStudentLessonAssignments: (studentId = null, params = {}) => {
    const queryParams = new URLSearchParams(params);
    if (studentId) {
      queryParams.append('student_id', studentId);
    }
    const queryString = queryParams.toString();
    const fullUrl = `/student-lesson-assignments${queryString ? '?' + queryString : ''}`;
    console.log('🌐 API Call: getStudentLessonAssignments', { fullUrl, params, studentId });
    return apiClient.get(fullUrl);
  },
  getStudentAssignmentsDirect: (studentId = null, params = {}) => {
    const queryParams = new URLSearchParams(params);
    if (studentId) {
      queryParams.append('student_id', studentId);
    }
    const queryString = queryParams.toString();
    const fullUrl = `/student-assignments-direct${queryString ? '?' + queryString : ''}`;
    console.log('🌐 API Call: getStudentAssignmentsDirect', { fullUrl, params, studentId });
    return apiClient.get(fullUrl);
  },
  getAssignmentDetails: (assignmentId) => apiClient.get(`/assignments/${assignmentId}/details`),
  getAssignmentQuestions: (assignmentId) => apiClient.get(`/assignments/${assignmentId}/questions`),
  submitAssignment: (assignmentId, submissionData) => apiClient.post(`/assignments/${assignmentId}/submit`, submissionData),
  getAssignmentResults: (assignmentId, attemptId) => apiClient.get(`/assignments/${assignmentId}/attempts/${attemptId}/results`),
  deleteCourseAssignment: (assignmentId) => apiClient.delete(`/course-assignments/${assignmentId}`),

  // Bookmarks
  createBookmark: (bookmarkData) => apiClient.post('/bookmarks', bookmarkData),
  getBookmarks: () => apiClient.get('/bookmarks'),
  deleteBookmark: (bookmarkId) => apiClient.delete(`/bookmarks/${bookmarkId}`),

  // Zoom Integration
  createZoomMeeting: (meetingData) => apiClient.post('/zoom/create-meeting', meetingData),
  getZoomMeeting: (meetingId) => apiClient.get(`/zoom/meetings/${meetingId}`),
  deleteZoomMeeting: (meetingId) => apiClient.delete(`/zoom/meetings/${meetingId}`),

  // Languages
  getLanguages: () => apiClient.get('/languages'),

  // Email Services
  sendPasswordResetEmail: (studentIds) => apiClient.post('/auth/send-password-reset-email', { student_ids: studentIds }),

  // Password Reset
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => apiClient.post('/auth/reset-password', { token, new_password: newPassword }),
  setPassword: (email, password) => apiClient.post('/auth/set-password', { email, password }),

  // Question Import/Export
  downloadQuestionTemplate: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.course_id) queryParams.append('course_id', params.course_id);
    if (params.module_id) queryParams.append('module_id', params.module_id);
    if (params.lesson_id) queryParams.append('lesson_id', params.lesson_id);
    if (params.topic_id) queryParams.append('topic_id', params.topic_id);
    if (params.content_id) queryParams.append('content_id', params.content_id);
    if (params.question_type) queryParams.append('question_type', params.question_type);
    
    return apiClient.post(`/questions/download-template${queryParams ? '?' + queryParams.toString() : ''}`, {}, {
      responseType: 'blob'
    });
  },
  importQuestions: (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const queryParams = new URLSearchParams();
    if (params.course_id) queryParams.append('course_id', params.course_id);
    if (params.module_id) queryParams.append('module_id', params.module_id);
    if (params.lesson_id) queryParams.append('lesson_id', params.lesson_id);
    if (params.topic_id) queryParams.append('topic_id', params.topic_id);
    if (params.content_id) queryParams.append('content_id', params.content_id);
    if (params.question_type) queryParams.append('question_type', params.question_type);
    
    const queryString = queryParams.toString();
    const url = `/questions/import${queryString ? `?${queryString}` : ''}`;
    return apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Instructor
  getInstructorStats: () => apiClient.get('/instructors/me/stats'),
  getInstructorDashboard: () => apiClient.get('/instructor/dashboard'),

  // Superadmin Courses
  getSuperadminCourses: () => apiClient.get('/courses?superadmin=true&institute_id=1'),
  purchaseCourse: (courseId) => apiClient.post('/courses/purchase', { course_id: courseId }),

  // Razorpay Payments
  getRazorpayKey: () => apiClient.get('/razorpay/key'),
  createRazorpayOrder: (orderData) => apiClient.post('/razorpay/create-order', orderData),
  verifyRazorpayPayment: (verificationData) => apiClient.post('/razorpay/verify-payment', verificationData),
  getRazorpayPaymentStatus: (paymentId) => apiClient.get(`/razorpay/payment-status/${paymentId}`),
  getExamPaymentStatus: () => apiClient.get('/payment-status'),

  // Wallet
  getWalletDashboard: (params = {}) => apiClient.get('/payment/wallet/dashboard', { params }),

  // Institute Settings
  getInstituteSettings: () => apiClient.get('/institute/settings'),
  updateInstituteSettings: (settings) => apiClient.put('/institute/settings', settings),

  // Exam Users Management
  getExamUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    if (params.exam_filter) queryParams.append('exam_filter', params.exam_filter);
    if (params.exam_session) queryParams.append('exam_session', params.exam_session);
    if (params.exam_instance) queryParams.append('exam_instance', params.exam_instance);
    if (params.show_inactive !== undefined) queryParams.append('show_inactive', params.show_inactive);
    
    const queryString = queryParams.toString();
    return apiClient.get(`/admin/exam-users${queryString ? '?' + queryString : ''}`);
  },
  saveUserNotes: (userId, notes) => apiClient.post(`/admin/exam-users/${userId}/notes`, { notes }),
};

export default api;
  // Programming Evaluation (using existing API structure)

  // Student Courses API
  // api.getStudentCourses = () => apiClient.get('/student/courses');
  // api.getStudentCourseDetails = (courseId) => apiClient.get(`/student/courses/${courseId}`);
  api.getMyCourses = () => apiClient.get('/enrollments/my-courses');
  api.getPurchasedCourses = () => apiClient.get('/student/purchased-courses');

  // Admin Courses API
  api.getAdminCourses = () => apiClient.get('/admin/courses');
  api.purchaseCourses = (purchaseData) => apiClient.post('/admin/purchase-courses', purchaseData);

  // Student Assignment Attempts
  api.getStudentBasicExams = (studentId) => apiClient.get(`/assignments/${studentId}/basic-exams`);
  api.getAssignmentAttempts = (assignmentId, studentId) => apiClient.get(`/assignments/${assignmentId}/attempts/${studentId}`);
